import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';

const StarContainer = styled(motion.div)`
  position: absolute;
  cursor: pointer;
  transform-style: preserve-3d;
`;

const SelectionCircle = styled(motion.div)`
  position: absolute;
  width: 30px;
  height: 30px;
  border: 2px solid white;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.8;
`;

const StarShape = styled.div`
  position: relative;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  animation: twinkle ${props => props.twinkleSpeed}s infinite alternate;

  /* Primary rays */
  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${props => props.size * (props.size < 4 ? 3 : 5)}px;
    height: ${props => props.size / (props.size < 4 ? 5 : 4)}px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, ${props => props.brightness * (props.size < 4 ? 0.7 : 1)}) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: translate(-50%, -50%);
  }

  /* Secondary rays at 45 degrees */
  .diagonal-rays {
    position: absolute;
    width: 100%;
    height: 100%;
    transform: rotate(45deg);
    opacity: ${props => props.size < 4 ? 0.5 : 1};

    &::before, &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: ${props => props.size * (props.size < 4 ? 2 : 3.5)}px;
      height: ${props => props.size / (props.size < 4 ? 8 : 6)}px;
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, ${props => props.brightness * (props.size < 4 ? 0.4 : 0.7)}) 50%,
        rgba(255, 255, 255, 0) 100%
      );
      transform: translate(-50%, -50%);
    }

    &::after {
      transform: translate(-50%, -50%) rotate(90deg);
    }
  }

  &::before {
    filter: blur(${props => props.glow * (props.size < 4 ? 0.3 : 0.5)}px);
  }

  &::after {
    filter: blur(${props => props.glow * (props.size < 4 ? 0.3 : 0.5)}px);
    transform: translate(-50%, -50%) rotate(90deg);
  }

  /* Central bright dot with intense glow */
  .star-core {
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${props => props.size / (props.size < 4 ? 2 : 1.5)}px;
    height: ${props => props.size / (props.size < 4 ? 2 : 1.5)}px;
    background: white;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    box-shadow: ${props => props.size < 4 ? `
      0 0 ${props.glow}px ${props.glow / 3}px rgba(255, 255, 255, 1),
      0 0 ${props.glow * 2}px ${props.glow / 2}px rgba(255, 255, 255, 0.4),
      0 0 ${props.glow * 3}px ${props.glow}px rgba(255, 255, 255, 0.2)
    ` : `
      0 0 ${props.glow * 2}px ${props.glow / 2}px rgba(255, 255, 255, 1),
      0 0 ${props.glow * 4}px ${props.glow}px rgba(255, 255, 255, 0.8),
      0 0 ${props.glow * 6}px ${props.glow * 2}px rgba(255, 255, 255, 0.6),
      0 0 ${props.glow * 8}px ${props.glow * 3}px rgba(255, 255, 255, 0.4)
    `};
  }

  @keyframes twinkle {
    0% {
      opacity: ${props => props.brightness * (props.size < 4 ? 0.5 : 0.7)};
      transform: scale(0.95);
    }
    100% {
      opacity: ${props => props.brightness};
      transform: scale(1.05);
    }
  }
`;

export const Star = ({ article, onClick, isSelected }) => {
  return (
    <StarContainer
      style={{
        left: `${article.position.x}%`,
        top: `${article.position.y}%`,
      }}
      onClick={onClick}
      whileHover={{ scale: 1.5 }}
    >
      {isSelected && (
        <SelectionCircle
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0 }}
        />
      )}
      <StarShape 
        size={article.size || 8}
        brightness={article.brightness || 0.8}
        glow={article.glow || 1}
        twinkleSpeed={article.twinkleSpeed || 1.5}
      >
        <div className="diagonal-rays" />
        <div className="star-core" />
      </StarShape>
    </StarContainer>
  );
}; 